import styles from './popup.module.scss';
import {
  useEffect,
  useRef,
  useState
} from "react";
import ReactDOM from "react-dom";
import { BoundingClientRectObserver } from "@html-ng/bounding-client-rect-observer";
import { CSSTransition } from "react-transition-group";

type PopupProps = {
  parent: HTMLDivElement | null;
  isShow: boolean;
  onClose: () => void;
  child: JSX.Element;
  width?: number;
  top?: number;
  left?: number;
  classContainer?: string;
}

export const Popup = ({ isShow, onClose, child, left, top, width, parent, classContainer }: PopupProps) => {

  const containerRef = useRef<HTMLDivElement>( null);

  const [offset, setOffset] = useState<{left: number; top: number}>();

  useEffect(() => {
    if (isShow) {
      setTimeout(() => {
        window.addEventListener("click", handleClickOutside);
        window.addEventListener("resize", resizeWindow);
      });
      calculateOffset();
    } else {
      setOffset(undefined)
    }
    return () => {
      window.removeEventListener("click", handleClickOutside);
      window.removeEventListener("resize", resizeWindow);
    }
  }, [isShow]);

  useEffect(() => {
    const observer = new BoundingClientRectObserver(calculateOffset)
    if (!!parent) {
      observer.observe(parent);
    }
    return () => {
      observer.disconnect();
    }
  }, [parent]);

  const resizeWindow = (): void => {
    calculateOffset();
  }

  const calculateOffset = (): void => {
    const newOffset = getOffset(parent);
    if (!!parent) {
      const bottomTop = newOffset.top + parent.clientHeight;
      const containerHeight = containerRef.current?.clientHeight as number;
      if (bottomTop + containerHeight > window.innerHeight) {
        newOffset.top -= containerHeight + parent.clientHeight
      } else {
        newOffset.top = bottomTop;
      }
    }
    if (!!left) {
      newOffset.left += left;
    }
    if (!!top) {
      newOffset.top += top;
    }
    setOffset(newOffset);
  }

  const getOffset = (el?: HTMLElement | null) => {
    const rect = el?.getBoundingClientRect();
    let x = rect?.left || 0;
    let y = rect?.top || 0;
    return { top: y, left: x };
  };

  const handleClickOutside = (e: Event) => {
    if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
      onClose();
    }
  }

  const getContainerStyle = (): string => {
    let result = styles.container;
    if (!!classContainer) {
      result += ` ${classContainer}`;
    }
    return result;
  }

  return ReactDOM.createPortal(
    <CSSTransition in={isShow} timeout={300} unmountOnExit nodeRef={containerRef} classNames={{
      enter: styles.enter,
      exit: styles.exit,
      enterActive: styles.enterActive,
      exitActive: styles.exitActive,
    }}>
      <div className={getContainerStyle()}
           style={{top: offset?.top, left: offset?.left, width: width}}
           ref={containerRef}>
        {child}
      </div>
    </CSSTransition>,
    document.body
  );
}